import { useState, useCallback } from "react";

/**
 * Generic message type that must have an id and timestamp
 */
export interface QueuedMessage {
  id: string;
  timestamp: number;
  [key: string]: any; // Allow additional properties
}

/**
 * Configuration for message queue
 */
export interface MessageQueueConfig {
  /**
   * Maximum queue size before dropping old messages
   * If queue exceeds this, only the latest message is kept
   * Default: 2
   */
  maxQueueSize?: number;
}

/**
 * Hook for managing a queue of messages
 * Animations control their own lifecycle via onComplete callback
 *
 * @param config Configuration for the message queue
 * @returns Queue state and management functions
 */
export function useMessageQueue<T extends QueuedMessage>(
  config: MessageQueueConfig
) {
  const { maxQueueSize = 2 } = config;
  const [queue, setQueue] = useState<T[]>([]);

  /**
   * Add messages to the queue
   * Enforces max queue size
   */
  const addMessages = useCallback(
    (messages: T[]) => {
      if (messages.length > 0) {
        console.log(`[useMessageQueue] Adding ${messages.length} message(s) to queue:`, messages.map(m => ({ id: m.id, type: (m as any).type })));
        setQueue((prevQueue) => {
          const updatedQueue = [...prevQueue, ...messages];

          // If queue exceeds max size, keep only the latest message
          if (updatedQueue.length > maxQueueSize) {
            const finalQueue = [updatedQueue[updatedQueue.length - 1]];
            console.log(`[useMessageQueue] Queue exceeded max size (${updatedQueue.length} > ${maxQueueSize}), keeping only latest`);
            return finalQueue;
          }

          return updatedQueue;
        });
      }
    },
    [maxQueueSize]
  );

  /**
   * Remove a message from the queue by ID
   */
  const removeMessage = useCallback((id: string) => {
    setQueue((prevQueue) => {
      const newQueue = prevQueue.filter((msg) => msg.id !== id);
      if (prevQueue.length !== newQueue.length) {
        console.log(`[useMessageQueue] Removed message ${id}, queue length: ${newQueue.length}`);
      }
      return newQueue;
    });
  }, []);

  /**
   * Clear all messages from the queue
   */
  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  /**
   * Get the current active message (first in queue)
   */
  const activeMessage = queue.length > 0 ? queue[0] : null;
  
  // activeMessage changes silently

  return {
    queue,
    activeMessage,
    addMessages,
    removeMessage,
    clearQueue,
  };
}
